/**
 * 文档模块公共逻辑：Markdown 渲染 + 目录（TOC）+ 滚动高亮 + 全屏。
 *
 * 抽取原因：DocsView 与 DocShareView 原本各自维护一套几乎相同的
 * 渲染 / scrollspy / 全屏代码（约 600 行重复），改一处极易忘另一处。
 *
 * 安全：渲染开启 html:true，内容会经 DOMPurify 消毒后再输出，
 * 防止文档中插入恶意脚本（分享页公开可访问，必须处理）。
 */
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import MarkdownIt from 'markdown-it'
import markdownItAnchor from 'markdown-it-anchor'
import DOMPurify from 'dompurify'

export interface TocItem {
  id: string
  text: string
  level: number
}

/**
 * 中文友好的 slug：保留中文/字母/数字，其余连续字符折叠为 '-'。
 * 旧的 `[^\w]+` 会把中文整体吃掉，导致所有中文标题的锚点都是 '-'，目录跳转全错。
 */
function slugify(s: string): string {
  const slug = s
    .trim()
    .toLowerCase()
    .replace(/[^\u4e00-\u9fa5a-z0-9_]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'section'
}

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true
}).use(markdownItAnchor, {
  level: [1, 2, 3, 4],
  slugify,
  permalink: markdownItAnchor.permalink.headerLink()
})

// 统一给文档内链接加安全属性，新窗口打开
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

/** 纯渲染函数：Markdown → 消毒后的 HTML（管理后台预览也复用它）。 */
export function renderDocHtml(raw: string): string {
  const dirty = md.render(raw)
  return DOMPurify.sanitize(dirty)
}

/** 从渲染结果提取目录（在消毒后的 DOM 上解析，保证 id 与实际输出一致）。 */
export function extractToc(html: string): TocItem[] {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const headings = doc.querySelectorAll('h1, h2, h3, h4')
  return Array.from(headings)
    .map((h) => ({
      id: h.id,
      text: h.textContent || '',
      level: parseInt(h.tagName.substring(1), 10)
    }))
    .filter((h) => h.id)
}

export function useMarkdownDoc() {
  const renderedContent = ref('')
  const toc = ref<TocItem[]>([])
  const activeHeadingId = ref('')

  /** 目录列表容器（用于高亮项自动滚动到可视区），由调用方通过 ref 绑定。 */
  const tocList = ref<HTMLElement | null>(null)
  /** 文档内容滚动容器，由调用方通过 ref 绑定。 */
  const scrollContainer = ref<HTMLElement | null>(null)

  const isFullscreen = ref(false)

  let scrollHandler: (() => void) | null = null
  let spyTimer: ReturnType<typeof setTimeout> | null = null

  // 渲染内容并重建目录与滚动高亮
  function renderMarkdown(raw: string) {
    const html = renderDocHtml(raw)
    renderedContent.value = html
    toc.value = extractToc(html)
    cleanupScrollSpy()
    // 等待 DOM 更新后再绑定 scrollspy
    spyTimer = setTimeout(setupScrollSpy, 100)
  }

  function scrollToHeading(id: string) {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
      activeHeadingId.value = id
    }
  }

  function setupScrollSpy() {
    const headings = toc.value
      .map((h) => document.getElementById(h.id))
      .filter((h): h is HTMLElement => Boolean(h))
    if (headings.length === 0) return
    const container = scrollContainer.value
    if (!container) return

    const onScroll = () => {
      let current = ''
      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= 100) {
          current = heading.id
        }
      }
      if (current && current !== activeHeadingId.value) {
        activeHeadingId.value = current
      }
    }

    container.addEventListener('scroll', onScroll)
    scrollHandler = onScroll
    onScroll()
  }

  function cleanupScrollSpy() {
    if (spyTimer) {
      clearTimeout(spyTimer)
      spyTimer = null
    }
    if (scrollHandler && scrollContainer.value) {
      scrollContainer.value.removeEventListener('scroll', scrollHandler)
      scrollHandler = null
    }
  }

  // 现代浏览器全部支持标准全屏 API，不再需要 -moz-/-webkit-/-ms- 前缀
  function toggleFullscreen() {
    const container = scrollContainer.value
    if (!container) return
    if (!document.fullscreenElement) {
      container.requestFullscreen?.()
    } else {
      document.exitFullscreen?.()
    }
  }

  function handleFullscreenChange() {
    isFullscreen.value = !!document.fullscreenElement
  }

  // 目录高亮变化时，保证高亮项在目录可视区内
  watch(activeHeadingId, (newId) => {
    if (!newId || !tocList.value) return
    nextTick(() => {
      const activeItem = tocList.value?.querySelector('li.active')
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    })
  })

  onMounted(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange)
  })

  onUnmounted(() => {
    cleanupScrollSpy()
    document.removeEventListener('fullscreenchange', handleFullscreenChange)
  })

  return {
    renderedContent,
    toc,
    activeHeadingId,
    tocList,
    scrollContainer,
    isFullscreen,
    renderMarkdown,
    scrollToHeading,
    toggleFullscreen
  }
}
